import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import Hotjar from '@hotjar/browser'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useGetCart } from './useGetCart'
import { onCartResponse } from '../../utils/onCartResponse'
import {
  clearCatalogueCart,
  setSynchronizingCart,
} from '../../redux/slices/catalog'
import { generateDataDogContext } from '../../utils/dataDog'
import useCartEmpty from '../../graphQL/mutations/cartEmpty'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import { addAlert } from '../../redux/slices/config'
import { sendGoogleEvent } from '../../utils/sendGoogleAnalytics'
import { EMPTY_STRING } from '../../consts/defaultConfigValues/defaultConstants'
import getAnalyticsItems from '../../utils/getAnalyticsItems'
import { fireGTMEvent } from '../../utils/fireGTMEvent'

export const useClearCart = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const state = useAppSelector((stateRedux) => stateRedux)
  const getCartAction = useGetCart()
  const {
    defaultSlice: { storeName, sessionId },
  } = state
  const currency = useAppSelector(
    (state) => state.defaultSlice.config?.options?.currencyCode || '',
  )
  const cartState = useAppSelector((state) => state.cartSlice)
  const { config } = state.defaultSlice
  const category = 'cart'
  const action = 'Clean Cart'

  /*
   * Reads the store when the response lands, not the render-time snapshot.
   * The clear is emitted from a dialog without being awaited, so the shopper
   * can leave the cart while it is in flight, and Apollo still calls this
   * when it answers.
   *
   * A snapshot from a page that has since unmounted still holds that page's
   * lists, and `onCartResponse` would write them back over the next page's:
   * among them the catalogue list, the open product and the category-nav
   * list. Which way the category-nav list goes wrong depends on the old page.
   * One that never loads it, such as the cart, holds it as `null`, so the
   * next page's list is skipped rather than updated; a category-nav page has
   * it refilled by `useGetProductsNav`, so its own list is written back over
   * the next page's. See `utils/onCartResponse.ts` for the full set, and
   * `useDeleteProductFromCart` (`useDeleteItemFromCart.ts`) for the same
   * hazard.
   */
  const onCompleted = () => {
    onCartResponse({
      data: {
        items: [],
        groups: [],
        total: 0,
        subtotal: 0,
        totalFee: 0,
        deferredPromotions: [],
        status: EMPTY_STRING,
        createdAt: EMPTY_STRING,
        updatedAt: EMPTY_STRING,
        id: EMPTY_STRING,
        activePromotions: [],
        activeFees: [],
      },
      dispatch,
      state: store.getState(),
    })
  }

  const onError = (error: ApolloError) => {
    const message = getAlertErrorMessage(
      error.networkError
        ? AlertMessageType.Network
        : AlertMessageType.RemoveItem,
      config?.texts,
    )
    dispatch(addAlert({ message }))
    getCartAction()
    const context = generateDataDogContext({
      title: 'Error on clear cart mutation',
      extraInfo: { function: 'clearCartAction > onError' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [cartEmptyFn] = useCartEmpty({ onCompleted, onError })

  const clearCartAction = async () => {
    try {
      dispatch(setSynchronizingCart(true))
      Hotjar.event(action)
      sendGoogleEvent(category, action)
      window.fbq('trackCustom', 'CleanCart')
      fireGTMEvent('commerce-remove_from_cart', {
        currency,
        value: cartState.total,
        items: getAnalyticsItems(cartState),
      })
      await cartEmptyFn({
        variables: {
          sessionUid: sessionId || '',
          storefrontName: storeName || '',
        },
      })
      dispatch(clearCatalogueCart())
    } catch (exception) {
      const context = generateDataDogContext({
        title: 'could not clear cart',
        extraInfo: { function: 'clearCartAction' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
    } finally {
      dispatch(setSynchronizingCart(false))
    }
  }
  return clearCartAction
}

export default useClearCart
